/**
 * PostHog interaction autocapture — scoped to a short allowlist of product surfaces.
 *
 * Autocapture used to be a flat `autocapture: false` in `monitorToolsSetup.js` (PR #3473,
 * turned off for event cost). It is now enabled ONLY on the surfaces listed below, via
 * posthog-js's `autocapture.url_allowlist`.
 *
 * How posthog-js applies this list (verified against posthog-js 1.406.2 —
 * `src/autocapture.ts` + `browser-common/utils/autocapture-utils.mjs`):
 *
 *   - Each entry is compiled with `new RegExp(entry)` — no flags — and tested with
 *     `url.match(regex)` where `url` is the FULL `window.location.href`, not the pathname.
 *     Hence the `^https?://[^/?#]+` prefix on every generated entry: it is host-AGNOSTIC
 *     (any host, so no environment is hardcoded) but still pins the path to the start of
 *     the path component. That matters in this app specifically, in two ways a bare `/logs`
 *     would get wrong: the console passes paths around in `next_url` query params (see
 *     `utils/url-validation.ts`), so `/login?next_url=/logs` would switch autocapture on for
 *     the login page; and a bare `/sip-trunking` matches ANYWHERE in the path, so it would
 *     also fire on `/voice-settings/sip-trunking` and `/geo-permissions/sip-trunking` —
 *     both deliberately excluded below. Both cases are pinned in the test file.
 *   - The check runs per DOM event, at capture time, so SPA navigation is handled for free:
 *     the same listeners stay attached and simply stop matching when you leave a surface.
 *   - A non-empty object makes `autocapture` truthy, which only clears the CLIENT gate.
 *     Autocapture is ALSO gated server-side by the project's `autocapture_opt_out` — see
 *     the note in `monitorToolsSetup.js`.
 *
 * Host-agnostic means this covers BOTH deployed consoles — prod (`cx.plivo.com`) and dev
 * (`cx.dev.contactodev.com`), each on its own PostHog project. A developer's machine is not
 * a concern: `monitorToolsSetup.js` never calls `posthog.init` when `isLocalhost`.
 *
 * Everything not listed here — billing, settings, conversations, analytics, onboarding,
 * auth — captures no interaction events at all.
 */

/**
 * Autocapture-enabled surfaces, as path prefixes. Each prefix covers the page itself and
 * every URL beneath it (`/logs` → `/logs/voice/<call_uuid>`), but stops at a path-segment
 * boundary, so `/logs` never matches `/logs-export`.
 *
 * Route paths live in `v2/configs/routes/main.tsx` / `ai-studio.tsx`; keep this list in
 * step with them when a surface moves.
 */
export const AUTOCAPTURE_PATH_PREFIXES = [
  // Home
  '/home',
  // AI agents: the list, plus the sibling `/agent/*` build surfaces it links into —
  // builder canvas (`/agent/builder/:id/*`), legacy builder, knowledge bases, secrets.
  '/agents',
  '/agent',
  // Agent runs (list + `/agent-runs/:flowId/:runId` detail)
  '/agent-runs',
  // SIP Trunking (`/sip-trunking/:tab`). NOT `/sip-trunking-settings`, which is a
  // separate settings page rather than a sub-URL of this one.
  '/sip-trunking',
  // Applications
  '/applications',
  // Phone numbers (`/phone-numbers/:tab`), plus the port-in order flow it owns
  // (`/port-in/new`, `/port-in/:requestId`), which sits on a sibling path.
  '/phone-numbers',
  '/port-in',
  // Logs — every tab and every insights detail page (`/logs/voice/:call_uuid`, …)
  '/logs',
  // Chat widget (`/chat-widget/list`, `/chat-widget/:id`)
  '/chat-widget',
] as const

/**
 * Escape a literal string for use inside a RegExp source. Exported for its test: no path
 * above currently contains a regex metacharacter, so this is defensive — but a future
 * `/v1.5/…` route would silently match `/v125/…` without it.
 */
export const escapeRegExp = (literal: string): string =>
  literal.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/**
 * `autocapture.url_allowlist` for `posthog.init`. Regex SOURCES (strings), not RegExp
 * objects — posthog-js compiles them itself.
 *
 * `^https?://[^/?#]+` matches any scheme+authority, so the path is pinned to the start of the
 * path component without naming an environment. The authority class excludes `?` and `#`, not
 * just `/`: with a bare `[^/]+` it could swallow a query or fragment and let a root URL that
 * merely mentions a route match — `https://any.host?x=/logs` did match before this was
 * tightened. `(?:[/?#]|$)` is the trailing path-segment boundary: it admits the bare page, any
 * child path, a query string and a hash, and rejects a longer sibling segment.
 */
export const autocaptureUrlAllowlist: string[] = AUTOCAPTURE_PATH_PREFIXES.map(
  (prefix) => `^https?://[^/?#]+${escapeRegExp(prefix)}(?:[/?#]|$)`,
)

/**
 * Mirrors posthog-js's own check (`checkForURLMatches`) so the allowlist is testable
 * without booting posthog. Not used at runtime — posthog-js does the matching.
 */
export const isAutocaptureUrlAllowed = (href: string): boolean =>
  autocaptureUrlAllowlist.some((source) => new RegExp(source).test(href))
